import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY, GOLD } from '../utils/constants';
import { componentsSum, isBalanced, exportTallyExcel } from '../utils/tallyExport';
import { CircleCheck, TriangleAlert, FileSpreadsheet, Search } from 'lucide-react';

// ─── Date helpers ─────────────────────────────────────────────────────────────
// invoices.invoice_date is stored as ISO YYYY-MM-DD (written from
// <input type="date"> in InvoiceModal), so range filters compare directly.
const todayInput = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};
const toDisplayDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).split('-');
  if (!y || !m || !d) return String(iso);
  return `${d}-${m}-${y}`;
};

const fmtAmount = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const sanitizeFilePart = (s) => String(s || '').replace(/[\\/:*?"<>|]/g, '-');

// ─── Styles (matches RangeReports) ────────────────────────────────────────────
const card = { background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14, padding: '18px 22px', marginBottom: 18, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' };
const sectionLabel = { fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 14 };
const thStyle = { padding: '8px 12px', fontWeight: 700, fontSize: 'var(--text-xs)', color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--color-sunken)', borderBottom: '1px solid var(--color-border-strong)' };
const tdStyle = { padding: '8px 12px', fontSize: 'var(--text-sm)', color: 'var(--color-ink-brand-softer)', borderBottom: '1px solid var(--color-border-subtle)', verticalAlign: 'middle' };
const tdNumStyle = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 };
const tableWrap = { overflowX: 'auto', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', marginBottom: 10 };
const tblStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' };
const inputStyle = { background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '9px 12px', color: NAVY, fontSize: '0.9rem', fontWeight: 600, outline: 'none', colorScheme: 'light' };
const fieldLabel = { fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-ink-muted)' };

const modeBtnStyle = (active) => ({
  padding: '8px 14px',
  background: active ? NAVY : '#fff',
  color: active ? '#fff' : 'var(--color-ink-secondary)',
  border: `1.5px solid ${active ? NAVY : 'var(--color-border-strong)'}`,
  borderRadius: 8,
  fontSize: '0.82rem',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'background 0.12s, color 0.12s',
});

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function TallyExport({ onNavigate }) {
  const [mode, setMode] = useState('range');            // 'single' | 'range'
  const [invoiceNo, setInvoiceNo] = useState('');
  const [fromDate, setFromDate] = useState(todayInput());
  const [toDate, setToDate] = useState(todayInput());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoices, setInvoices] = useState(null);       // null = not fetched yet
  const [exportSummary, setExportSummary] = useState(null);

  const rangeInvalid = mode === 'range' && fromDate && toDate && fromDate > toDate;
  const canFetch = mode === 'single' ? invoiceNo.trim().length > 0 : (fromDate && toDate && !rangeInvalid);

  const balanced = (invoices || []).filter(isBalanced);
  const unbalanced = (invoices || []).filter((inv) => !isBalanced(inv));
  const partyLedgers = Array.from(new Set(balanced.map((inv) => inv.partner_legal_name).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b));

  const fetchInvoices = async () => {
    if (!canFetch || loading) return;
    setLoading(true);
    setError('');
    setInvoices(null);
    setExportSummary(null);
    try {
      let query = supabase.from('invoices').select('*').eq('invoice_type', 'b2b');
      if (mode === 'single') {
        query = query.eq('invoice_number', invoiceNo.trim());
      } else {
        query = query.gte('invoice_date', fromDate).lte('invoice_date', toDate);
      }
      const { data, error: qErr } = await query
        .order('invoice_date', { ascending: true })
        .order('invoice_number', { ascending: true });
      if (qErr) throw qErr;
      setInvoices(data || []);
    } catch (err) {
      console.error('Invoice fetch failed:', err);
      setError('Could not load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!invoices || balanced.length === 0) return;
    const filenameBase = mode === 'single'
      ? `AccountingVoucher_${sanitizeFilePart(invoiceNo.trim())}`
      : `AccountingVouchers_${toDisplayDate(fromDate)}_to_${toDisplayDate(toDate)}`;
    try {
      const result = exportTallyExcel(invoices, filenameBase);
      setExportSummary(result);
    } catch (err) {
      console.error('Tally export failed:', err);
      setError('Export failed. Please try again.');
    }
  };

  return (
    <div>
      <div className="back-row">
        <button className="back-btn" onClick={() => onNavigate('operations')}>← Operations</button>
      </div>

      <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: NAVY, marginBottom: 4 }}>Invoice Export (Tally)</h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', marginBottom: 20 }}>
        Export B2B invoices as a Tally-importable Accounting Voucher Excel file. Only balanced invoices
        (tax components summing to the grand total) are exported.
      </p>

      {/* Selection */}
      <div style={card}>
        <div style={sectionLabel}>Select Invoices</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <button style={modeBtnStyle(mode === 'range')} onClick={() => { setMode('range'); setInvoices(null); setExportSummary(null); }}>
            Date range
          </button>
          <button style={modeBtnStyle(mode === 'single')} onClick={() => { setMode('single'); setInvoices(null); setExportSummary(null); }}>
            Single invoice
          </button>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
          {mode === 'single' ? (
            <>
              <span style={fieldLabel}>Invoice number</span>
              <input
                type="text"
                value={invoiceNo}
                onChange={(e) => setInvoiceNo(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchInvoices()}
                placeholder="e.g. AG/2026-27/0001"
                style={{ ...inputStyle, minWidth: 240 }}
              />
            </>
          ) : (
            <>
              <span style={fieldLabel}>From</span>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inputStyle} />
              <span style={fieldLabel}>To</span>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inputStyle} />
            </>
          )}
          <button
            onClick={fetchInvoices}
            disabled={!canFetch || loading}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 18px',
              background: !canFetch || loading ? '#ccc' : NAVY, color: '#fff', border: 'none',
              borderRadius: 9, fontSize: '0.85rem', fontWeight: 700,
              cursor: !canFetch || loading ? 'not-allowed' : 'pointer',
            }}
          >
            <Search size={15} strokeWidth={2.25} aria-hidden="true" />
            {loading ? 'Loading…' : 'Load invoices'}
          </button>
        </div>

        {rangeInvalid && (
          <div style={{ fontSize: '0.78rem', color: '#dc2626', fontWeight: 600, marginTop: 8 }}>From date must be before To date.</div>
        )}
        {error && (
          <div style={{ fontSize: '0.78rem', color: '#dc2626', fontWeight: 600, marginTop: 8 }}>{error}</div>
        )}
      </div>

      {/* Empty state */}
      {invoices && invoices.length === 0 && (
        <div style={card}>
          <div style={{ fontSize: '0.85rem', color: 'var(--color-ink-muted)', textAlign: 'center', padding: '18px 0' }}>
            No B2B invoices found for this {mode === 'single' ? 'invoice number' : 'date range'}.
          </div>
        </div>
      )}

      {invoices && invoices.length > 0 && (
        <>
          {/* Preview */}
          <div style={card}>
            <SectionTitle title={`Invoices (${invoices.length})`} />
            <div style={tableWrap}>
              <table className="data-table" style={tblStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Invoice No.</th>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Party (Legal Name)</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Grand Total</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Balanced</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const ok = isBalanced(inv);
                    return (
                      <tr key={inv.id}>
                        <td style={{ ...tdStyle, fontWeight: 600, whiteSpace: 'nowrap' }}>{inv.invoice_number}</td>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{toDisplayDate(inv.invoice_date)}</td>
                        <td style={tdStyle}>{inv.partner_legal_name || '—'}</td>
                        <td style={tdNumStyle}>{fmtAmount(inv.grand_total)}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {ok
                            ? <CircleCheck size={16} strokeWidth={2.25} color="#166534" aria-label="Balanced" />
                            : <TriangleAlert size={16} strokeWidth={2.25} color="#dc2626" aria-label="Not balanced" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)' }}>
              {balanced.length} balanced · {unbalanced.length} excluded
            </div>
          </div>

          {/* Excluded */}
          {unbalanced.length > 0 && (
            <div style={{ ...card, border: '1px solid #fecaca', background: '#fff1f2' }}>
              <div style={{ ...sectionLabel, color: '#dc2626' }}>Excluded (do not balance — will NOT be exported)</div>
              <div style={tableWrap}>
                <table className="data-table" style={{ ...tblStyle, background: '#fff' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Invoice No.</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Grand Total</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Components Sum</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Discrepancy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unbalanced.map((inv) => {
                      const sum = componentsSum(inv);
                      const diff = sum - (Number(inv.grand_total) || 0);
                      return (
                        <tr key={inv.id}>
                          <td style={{ ...tdStyle, fontWeight: 600, whiteSpace: 'nowrap' }}>{inv.invoice_number}</td>
                          <td style={tdNumStyle}>{fmtAmount(inv.grand_total)}</td>
                          <td style={tdNumStyle}>{fmtAmount(sum)}</td>
                          <td style={{ ...tdNumStyle, color: '#dc2626' }}>{diff > 0 ? '+' : '−'}{fmtAmount(Math.abs(diff))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: '0.78rem', color: '#7a3a10' }}>
                Fix these invoices (tax components must sum to the grand total) and reload before exporting.
              </div>
            </div>
          )}

          {/* Party ledgers */}
          {partyLedgers.length > 0 && (
            <div style={card}>
              <SectionTitle title={`Party ledgers in this batch (${partyLedgers.length})`} />
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                {partyLedgers.map((name) => (
                  <li key={name} style={{ fontSize: '0.85rem', fontWeight: 600, color: NAVY, padding: '7px 12px', background: 'var(--color-sunken)', borderRadius: 8 }}>
                    {name}
                  </li>
                ))}
              </ul>
              <div style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)' }}>
                Confirm each exists in Tally (or approve auto-create) before importing.
              </div>
            </div>
          )}

          {/* Export */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18 }}>
            <button
              onClick={handleExport}
              disabled={balanced.length === 0}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px',
                background: balanced.length === 0 ? '#ccc' : '#166534', color: '#fff', border: 'none',
                borderRadius: 9, fontSize: '0.85rem', fontWeight: 700,
                cursor: balanced.length === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              <FileSpreadsheet size={16} strokeWidth={2} aria-hidden="true" />
              Export to Excel ({balanced.length} invoice{balanced.length === 1 ? '' : 's'})
            </button>
            {exportSummary && (
              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: GOLD }}>
                Exported {exportSummary.exportedCount} invoice{exportSummary.exportedCount === 1 ? '' : 's'}.{' '}
                {exportSummary.excluded.length} excluded. {exportSummary.partyLedgers.length} party ledger{exportSummary.partyLedgers.length === 1 ? '' : 's'}.
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SectionTitle({ title }) {
  return <div style={sectionLabel}>{title}</div>;
}
